import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@design-system';
import { QuestionnaireContent } from '@/features/client-quick-plan';

import { ChevronRight } from 'lucide-react';

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

type View = 'options' | 'questionnaire';

interface IncompleteIntakeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client: Client;
}

const OPTIONS = [
  {
    id: 'medical-questionnaire' as const,
    title: 'Complete Medical Questionnaire',
    description:
      'Fill out a detailed health questionnaire for personalized care projections and underwriting insights.',
    recommended: true,
  },
  {
    id: 'set-assumptions' as const,
    title: 'Set Care Assumptions',
    description:
      'Manually customize care assumptions such as care duration, level of care, and costs.',
    recommended: false,
  },
];

export function IncompleteIntakeDialog({
  open,
  onOpenChange,
  client,
}: IncompleteIntakeDialogProps) {
  const navigate = useNavigate();
  const [view, setView] = useState<View>('options');

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      setView('options');
    }
    onOpenChange(nextOpen);
  }

  function handleSelect(optionId: (typeof OPTIONS)[number]['id']) {
    switch (optionId) {
      case 'medical-questionnaire':
        setView('questionnaire');
        break;
      case 'set-assumptions':
        handleOpenChange(false);
        navigate(`/clients/${client.clientId}/onboarding/edit-assumptions?from=overview`);
        break;
    }
  }

  function handleContinueToResults() {
    handleOpenChange(false);
    navigate(`/clients/${client.clientId}/onboarding/overview`);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={handleOpenChange}
    >
      <DialogContent className="max-h-[90vh] max-w-[calc(100%-24px)] overflow-y-auto sm:max-w-lg">
        {view === 'options' && (
          <>
            <DialogHeader>
              <DialogTitle className="text-start">Incomplete Client Information</DialogTitle>
              <DialogDescription className="text-start">
                This client was created with basic information. For personalized projections, please
                complete the medical questionnaire, or customize the assumptions yourself.
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col gap-3">
              {OPTIONS.map(({ id, title, description, recommended }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => handleSelect(id)}
                  className="hover:border-primary hover:bg-primary/5 border-border bg-background group flex w-full cursor-pointer items-center gap-4 rounded-lg border p-4 text-left transition-all"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-950">{title}</h3>
                      {recommended && <Badge className="-translate-y-0.5">Recommended</Badge>}
                    </div>
                    <p className="text-foreground text-sm">{description}</p>
                  </div>
                  <ChevronRight className="group-hover:text-primary text-muted-foreground size-5 shrink-0 transition-all" />
                </button>
              ))}
            </div>

            <DialogFooter className="mt-4">
              <Button
                variant="secondary"
                className="ml-auto w-max"
                onClick={handleContinueToResults}
              >
                Continue to Client Results
              </Button>
            </DialogFooter>
          </>
        )}

        {view === 'questionnaire' && (
          <QuestionnaireContent
            client={client}
            onBack={() => setView('options')}
            onClose={() => handleOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
